import React, { useState } from "react";
import { PageLayout } from "../../components/PageLayout";
import CodeMirror from "react-codemirror";

import "codemirror/lib/codemirror.css";
import "codemirror/theme/darcula.css";
import "codemirror/mode/toml/toml";

import "./style.css";

import { Resume } from "../../Resume";
import { validate } from "../../validator";
import toml from "@iarna/toml";
import { sampleResume } from "../../sample-resume";
import { useDebouncedCallback } from "use-debounce";
import classnames from "classnames";

import { MdInput, MdFileDownload } from "react-icons/md";

import { BlobProvider } from "@react-pdf/renderer";
import ReactLoading from "react-loading";

/**
 * Why we had to use the lower level BlobProvider as opposed to PDFViewer/PDFLink
 * https://github.com/diegomura/react-pdf/issues/310
 *
 * Some code lifted from: https://github.com/diegomura/react-pdf/blob/master/src/dom.js
 */
export const Editor = () => {
  const [state, setState] = useState({
    editorValue: sampleResume,
    errors: [],
    rendererValue: toml.parse(sampleResume),
    collapsed: false
  });

  const [debouncedHandleChange] = useDebouncedCallback(
    newValue =>
      setState(prevState => {
        let errors = [];
        let parsed;
        let rendererValue = prevState.rendererValue;

        try {
          parsed = toml.parse(newValue);
        } catch (err) {
          errors = [
            {
              message: "Invalid TOML"
            }
          ];
        }

        if (!errors.length) {
          errors = validate(parsed) || [];
        }

        if (!errors.length) {
          rendererValue = parsed;
        }

        const nextState = {
          ...prevState,
          editorValue: newValue,
          errors,
          rendererValue
        };

        return nextState;
      }),
    500
  );

  const resumeDoc = <Resume content={state.rendererValue} />;

  return (
    <PageLayout title={"Editor"}>
      <BlobProvider document={resumeDoc}>
        {({ url, blob, error, loading }) => (
          <div className="flex flex-col h-full">
            <div className="flex flex-row bg-gray">
              <ValidationBlock
                errors={state.errors}
                className={classnames("w-2/4", {
                  "w-full": state.collapsed
                })}
              />
              <div className="flex items-center flex-1 justify-between text-xl text-white">
                <div
                  className={classnames(
                    "flex items-center h-full px-3 hover:bg-gray-lighter cursor-pointer",
                    {
                      "flip-horizontal": state.collapsed
                    }
                  )}
                  onClick={() =>
                    setState(prevState => ({
                      ...prevState,
                      collapsed: !prevState.collapsed
                    }))
                  }
                >
                  <MdInput />
                </div>

                {loading ? null : (
                  <div
                    className={classnames(
                      "flex items-center h-full px-3 hover:bg-gray-lighter cursor-pointer"
                    )}
                  >
                    <a
                      href={url}
                      download={"resume.pdf"}
                      onClick={downloadOnIE(blob)}
                    >
                      <MdFileDownload />
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="flex h-full">
              <div
                className={classnames("h-full flex flex-col w-2/4 max-w-100", {
                  "w-full": state.collapsed
                })}
              >
                <CodeMirror
                  className="w-full h-full"
                  value={state.editorValue}
                  onChange={newValue => debouncedHandleChange(newValue)}
                  options={{ lineNumbers: true, theme: "darcula" }}
                />
              </div>

              <div
                className={classnames("w-2/4 flex flex-col h-full relative", {
                  "w-0": state.collapsed
                })}
              >
                {loading ? (
                  <div className="h-full bg-black flex items-center justify-center">
                    <ReactLoading type="spin" color="#fff" />
                  </div>
                ) : (
                  <>
                    <p className="absolute p-3">
                      If you are seeing this message then it is likely PDF
                      previews are disabled in your browser. This can usually be
                      changed in your browser preferences.
                    </p>
                    <iframe src={url} className="absolute h-full" />
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </BlobProvider>
    </PageLayout>
  );
};

function ValidationBlock({ errors, className, style }) {
  return (
    <div className={className} style={style}>
      {errors.length ? (
        <div className="p-2 bg-red text-white min-height-10">
          {errors.map(err => {
            return (
              <div key={err.schemaPath + err.message}>
                <strong>Error: </strong>
                {err.message}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-2 bg-primary text-white min-height-10">
          <div>No errors, yay!</div>
        </div>
      )}
    </div>
  );
}

function downloadOnIE(blob) {
  if (window.navigator.msSaveBlob) {
    window.navigator.msSaveBlob(blob, fileName);
  }
}
